// sim/regions.ts — the REGION MANAGER: which zones are unlocked, how they open,
// and the region-specific spawn rolls. The world is a graph of seven regions; a
// colony expands OUTWARD along adjacency as it grows stronger (Backyard is the
// always-open starter). Region content (resources / enemies / gene drops) is
// data in regionData.ts; this file is the logic that gates and samples it.
//
// Determinism: unlock evaluation is pure integer comparison over the fixed
// REGION order; every spawn roll draws from a seeded Rng. No allocation in the
// roll/eval paths. The unlock state hashes to an integer for the world hash.

import type { GeneType, ColonyId } from '../types.ts';
import type { Rng } from '../rng.ts';
import type { ColonySystem } from './colony.ts';
import type { RegionDef, ResourceEntry, EnemyEntry } from './regionData.ts';
import { fnvInit, fnvU32 } from '../hash.ts';

/** The seven regions, in adjacency/progression order. Dense 0-based indices. */
export const REGION = {
  Backyard: 0,
  Garden: 1,
  CompostHeap: 2,
  Shed: 3,
  Pond: 4,
  HouseInterior: 5,
  RoofGutters: 6,
} as const;
export type Region = (typeof REGION)[keyof typeof REGION];
export const REGION_COUNT = 7;

/** Food units that count as one point of "colony strength" (the unlock metric). */
export const FOOD_PER_STRENGTH = 5;

/**
 * A colony's strength: bodies on the ground plus a fraction of its food stockpile.
 * Integer, deterministic — the signal that drives region unlocks.
 */
export function colonyStrength(colonies: ColonySystem, cid: ColonyId): number {
  return colonies.populationAt(cid) + Math.floor(colonies.foodAt(cid) / FOOD_PER_STRENGTH);
}

/** The strongest colony's strength (the leading colony drives expansion). */
export function peakStrength(colonies: ColonySystem): number {
  let best = 0;
  for (let c = 0; c < colonies.count; c++) {
    const s = colonyStrength(colonies, c as ColonyId);
    if (s > best) best = s;
  }
  return best;
}

/** Weighted pick over a parallel weight array (allocation-free, deterministic). */
function pickWeighted(weights: readonly number[], rng: Rng): number {
  let sum = 0;
  for (let i = 0; i < weights.length; i++) sum += weights[i] as number;
  if (sum <= 0) return 0;
  let roll = rng.int(sum);
  for (let i = 0; i < weights.length; i++) {
    roll -= weights[i] as number;
    if (roll < 0) return i;
  }
  return weights.length - 1;
}

export class RegionManager {
  readonly defs: readonly RegionDef[];
  /** 1 if a region is unlocked, indexed by Region. */
  private readonly unlocked: Uint8Array;
  private unlockedN: number;

  constructor(defs: readonly RegionDef[]) {
    if (defs.length !== REGION_COUNT) {
      throw new Error(`RegionManager needs ${REGION_COUNT} region defs, got ${defs.length}`);
    }
    this.defs = defs;
    this.unlocked = new Uint8Array(REGION_COUNT);
    // The starter zone is open from tick zero.
    this.unlocked[REGION.Backyard] = 1;
    this.unlockedN = 1;
  }

  // --- Unlock state ----------------------------------------------------------

  isUnlocked(r: Region): boolean {
    return this.unlocked[r] === 1;
  }

  get unlockedCount(): number {
    return this.unlockedN;
  }

  def(r: Region): RegionDef {
    return this.defs[r] as RegionDef;
  }

  /** True if any region adjacent to `r` is already unlocked. */
  hasUnlockedNeighbor(r: Region): boolean {
    const nbs = (this.defs[r] as RegionDef).neighbors;
    for (let i = 0; i < nbs.length; i++) {
      if (this.unlocked[nbs[i] as number] === 1) return true;
    }
    return false;
  }

  /**
   * Unlock every region now eligible — strength meets its threshold AND it is
   * adjacent to an already-unlocked region (the "adjacent expansion" rule). A
   * single call cascades the frontier outward as far as the strength allows.
   * Returns the number of regions newly unlocked. Deterministic.
   */
  evaluate(strength: number): number {
    let total = 0;
    let changed = true;
    while (changed) {
      changed = false;
      for (let r = 0; r < REGION_COUNT; r++) {
        if (this.unlocked[r] === 1) continue;
        const def = this.defs[r] as RegionDef;
        if (strength < def.unlockStrength) continue;
        if (!this.hasUnlockedNeighbor(r as Region)) continue;
        this.unlocked[r] = 1;
        this.unlockedN++;
        total++;
        changed = true;
      }
    }
    return total;
  }

  /** Convenience: evaluate against the strongest colony's current strength. */
  evaluateColonies(colonies: ColonySystem): number {
    return this.evaluate(peakStrength(colonies));
  }

  // --- Region-specific spawn tables (deterministic rolls) --------------------

  /** Roll a resource node from a region's table. */
  rollResource(r: Region, rng: Rng): ResourceEntry {
    const list = (this.defs[r] as RegionDef).resources;
    let sum = 0;
    for (let i = 0; i < list.length; i++) sum += (list[i] as ResourceEntry).weight;
    let roll = rng.int(sum);
    for (let i = 0; i < list.length; i++) {
      roll -= (list[i] as ResourceEntry).weight;
      if (roll < 0) return list[i] as ResourceEntry;
    }
    return list[list.length - 1] as ResourceEntry;
  }

  /** Roll an enemy from a region's table. */
  rollEnemy(r: Region, rng: Rng): EnemyEntry {
    const list = (this.defs[r] as RegionDef).enemies;
    let sum = 0;
    for (let i = 0; i < list.length; i++) sum += (list[i] as EnemyEntry).weight;
    let roll = rng.int(sum);
    for (let i = 0; i < list.length; i++) {
      roll -= (list[i] as EnemyEntry).weight;
      if (roll < 0) return list[i] as EnemyEntry;
    }
    return list[list.length - 1] as EnemyEntry;
  }

  /**
   * Roll which gene base TYPE (GENE_TYPE.*) drops in a region — the region's
   * gene-drop table. The loot forge pairs this with `rarityBias(r)` to set
   * quality. (Seam to src/sim/geneForge.ts; regions decide WHAT body part, the
   * forge decides how good.)
   */
  rollGeneType(r: Region, rng: Rng): GeneType {
    const w = (this.defs[r] as RegionDef).geneWeights;
    return pickWeighted(w, rng) as GeneType;
  }

  /** Rarity bias a region adds to gene drops (deeper regions → better genes). */
  rarityBias(r: Region): number {
    return (this.defs[r] as RegionDef).rarityBias;
  }

  // --- Determinism fingerprint ----------------------------------------------

  /** FNV-1a fingerprint of the unlock state (fold into a world hash). */
  hash(): number {
    let h = fnvInit();
    for (let r = 0; r < REGION_COUNT; r++) h = fnvU32(h, this.unlocked[r] as number);
    return h >>> 0;
  }
}
